import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { ArrowLeft, Save, Sparkles } from 'lucide-react';

export function AssociadoNovoPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    documento: '', nome: '', matriculaServidor: '', orgaoPublico: '',
    situacaoServidor: 'ativo', celular: '', email: '', dataNascimento: '',
    cep: '', endereco: '', cidade: '', uf: '',
  });
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    // Pré-preenchimento via query string (vindo da tela sem-match)
    const cpf = params.get('cpf');
    const nome = params.get('nome');
    const matricula = params.get('matricula');
    if (cpf || nome || matricula) {
      setForm(f => ({
        ...f,
        documento: cpf ?? f.documento,
        nome: nome ?? f.nome,
        matriculaServidor: matricula ?? f.matriculaServidor,
      }));
      setPrefilled(true);
    }
  }, [params]);

  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (data: any) => api<{ id: string }>('/associados/', { method: 'POST', body: data }),
    onSuccess: async (result) => {
      // Roda rematch automático em todas as importações pra vincular linhas pendentes do CPF
      await api('/importacao/rematch-todos', { method: 'POST' }).catch(() => {});
      qc.invalidateQueries({ queryKey: ['sem-match'] });
      qc.invalidateQueries({ queryKey: ['importacoes'] });
      navigate(`/associados/${result.id}`);
    },
    onError: (err: ApiException) => {
      const msg = err.apiError?.message ?? 'Falha ao cadastrar';
      const details = (err.apiError?.details as any)?.fieldErrors;
      if (details) {
        const fieldMsgs = Object.entries(details).map(([k, v]: any) => `${k}: ${v.join(', ')}`).join(' · ');
        setError(`${msg} (${fieldMsgs})`);
      } else { setError(msg); }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const payload: any = {
      documento: form.documento.replace(/\D/g, ''),
      nome: form.nome.trim(),
      matriculaServidor: form.matriculaServidor.trim(),
    };
    if (form.orgaoPublico) payload.orgaoPublico = form.orgaoPublico;
    if (form.situacaoServidor) payload.situacaoServidor = form.situacaoServidor;
    if (form.celular) payload.celular = form.celular;
    if (form.email) payload.email = form.email;
    if (form.dataNascimento) payload.dataNascimento = form.dataNascimento;
    if (form.cep) payload.cep = form.cep.replace(/\D/g, '');
    if (form.endereco) payload.endereco = form.endereco;
    if (form.cidade) payload.cidade = form.cidade;
    if (form.uf) payload.uf = form.uf.toUpperCase();
    create.mutate(payload);
  }

  function field(key: keyof typeof form, label: string, type = 'text', placeholder = '') {
    return (
      <div className="space-y-1">
        <label className="text-sm font-medium" htmlFor={key}>{label}</label>
        <Input id={key} type={type} value={form[key]}
          onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
          placeholder={placeholder} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to={prefilled ? '/associados/sem-match' : '/associados'} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" />voltar
      </Link>
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Novo associado</h1>
        <p className="text-muted-foreground">Preencha os dados essenciais agora — o restante pode ser editado depois.</p>
      </div>

      {prefilled && (
        <div className="flex items-start gap-2 p-3 border border-blue-200 bg-blue-50 rounded-md text-sm">
          <Sparkles className="h-4 w-4 text-blue-700 mt-0.5" />
          <div className="text-blue-900">
            <p className="font-medium">Dados pré-preenchidos da importação ETIPI</p>
            <p className="text-blue-800 text-xs">CPF, nome e matrícula vieram do arquivo de retorno. Confira e complete os outros campos.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Identidade</CardTitle>
            <CardDescription>CPF é único e não pode ser alterado depois</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('documento', 'CPF *', 'text', 'somente números')}
              {field('nome', 'Nome completo *')}
              {field('matriculaServidor', 'Matrícula servidor *', 'text', 'ex: 007638-4')}
              {field('dataNascimento', 'Data de nascimento', 'date')}
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader><CardTitle className="text-lg">Vínculo público</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('orgaoPublico', 'Órgão')}
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="situacaoServidor">Situação</label>
                <select id="situacaoServidor" value={form.situacaoServidor}
                  onChange={(e) => setForm(f => ({ ...f, situacaoServidor: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">—</option>
                  <option value="ativo">ativo</option>
                  <option value="afastado">afastado</option>
                  <option value="aposentado">aposentado</option>
                  <option value="pensionista">pensionista</option>
                  <option value="comissionado">comissionado</option>
                  <option value="contratado">contratado</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader><CardTitle className="text-lg">Contato e endereço</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('celular', 'Celular', 'tel')}
              {field('email', 'E-mail', 'email')}
              {field('cep', 'CEP', 'text', 'somente números')}
              {field('endereco', 'Endereço')}
              {field('cidade', 'Cidade')}
              {field('uf', 'UF', 'text', 'ex: PI')}
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mt-4 p-3 border border-destructive/30 bg-destructive/10 rounded-md text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <><Spinner className="mr-2 h-4 w-4" />salvando…</> : <><Save className="h-4 w-4 mr-2" />Salvar</>}
          </Button>
          <Link to={prefilled ? '/associados/sem-match' : '/associados'}>
            <Button type="button" variant="ghost">Cancelar</Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
